import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  ButtonIcon,
  Icon,
  MobileDrawer,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import { setConsultActionAssigneeLoading } from '../../../redux/certdetails/action';
import { useInvertedLocalize } from '../../../utils/localization';
import { BULK_ACTION_REASSIGN } from '../mockData';

import BulkActionReassignBody from './BulkActionReassignBody';
import BulkActionReassignFooter from './BulkActionReassignFooter';
import BulkActionReassignHeader from './BulkActionReassignHeader';
import BulkActionReassignSubTitle from './BulkActionReassignSubTitle';
import { TRANSITION_TIMEOUT } from './constant';
import invertedMessages from './messages';

import './BulkActionReassign.css';

function BulkActionReassign({
  bulkFooterStats,
  bulkActionReassign,
  setBulkActionReassign,
  handleReassign,
  bulkPredefinedComments,
  isMobileXlargeWidth,
}) {
  const dispatch = useDispatch();
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);

  const l = useInvertedLocalize(invertedMessages);

  const handleCloseBulkActionReassign = () => {
    setTimeout(() => {
      dispatch(setConsultActionAssigneeLoading(true));
    }, TRANSITION_TIMEOUT);

    setBulkActionReassign((prev) => ({
      ...prev,
      isOpen: false,
      hasAgreed: false,
      reassignAssociated: false,
      reassignTo: '',
      comment: '',
    }));
  };

  const handleCloseBukActionReassignAlert = () => {
    setBulkActionReassign((prev) => ({
      ...prev,
      showAlert: false,
    }));
  };

  const bulkActionReassignModal = classnames('BulkActionReassignModal');

  useEffect(() => {
    if (!isMobileDrawerOpen && isMobileXlargeWidth)
      handleCloseBulkActionReassign();
  }, [isMobileDrawerOpen]);

  useEffect(() => {
    setIsMobileDrawerOpen(bulkActionReassign?.isOpen);
  }, [bulkActionReassign?.isOpen]);

  // TODO: @Sagar, These classNames are very important to update!!
  if (isMobileXlargeWidth) {
    return (
      <MobileDrawer
        className='BulkActionReassign-mobileDrawer'
        classNames={{
          headerClassName: 'BulkActionReassign-mobileDrawer--header',
          focusLockClassName: 'BulkActionReassign-mobileDrawer--focusLock',
          focusLockContentClassName:
            'BulkActionReassign-mobileDrawer--focusLockContent',
          contentClassName: 'BulkActionReassign-mobileDrawer--content',
        }}
        isOpen={isMobileDrawerOpen}
        setIsOpen={setIsMobileDrawerOpen}
        headerContent={
          <div className='ModalIsland-headerWrapper'>
            <div className='ModalIsland-header ModalIsland-header--hasSubtitle ModalIsland-header--hasIcon'>
              <Icon kind='AlertWarningFilledSeparateColors' size='large' />
              <div className='ModalIsland-titleContainer'>
                <Typography
                  className='ModalIsland-title'
                  kind='h2'
                  desktop='h2-d'
                  mobile='h2-d'>
                  <BulkActionReassignHeader
                    id={bulkActionReassign?.id}
                    bulkFooterStats={bulkFooterStats}
                  />
                </Typography>
                <Typography
                  className='ModalIsland-subtitle'
                  kind='body2'
                  desktop='body2-d'
                  mobile='body2-d'>
                  <BulkActionReassignSubTitle
                    id={bulkActionReassign?.id}
                    bulkFooterStats={bulkFooterStats}
                  />
                </Typography>
              </div>
            </div>
            <Box tag='div' className='ModalIsland-closeBtn'>
              <ButtonIcon
                size='medium'
                kind='ghost'
                ariaLabel={l('Close')}
                icon={
                  <Icon kind='close' color='neutral-100' size='smallMedium' />
                }
                onClick={() => setIsMobileDrawerOpen(false)}
              />
            </Box>
          </div>
        }
        content={
          <BulkActionReassignBody
            bulkFooterStats={bulkFooterStats}
            bulkActionReassign={bulkActionReassign}
            setBulkActionReassign={setBulkActionReassign}
            handleCloseBulkActionReassign={handleCloseBulkActionReassign}
            handleCloseBukActionReassignAlert={
              handleCloseBukActionReassignAlert
            }
            bulkPredefinedComments={bulkPredefinedComments}
          />
        }
        footerActionBar={
          <BulkActionReassignFooter
            handleReassign={handleReassign}
            bulkActionReassign={bulkActionReassign}
            handleCloseBulkActionReassign={handleCloseBulkActionReassign}
          />
        }
      />
    );
  }

  return (
    <ModalIsland
      className={bulkActionReassignModal}
      classNames={{
        headerClassName: 'BulkActionReassignModal-header',
        bodyClassName: 'BulkActionReassignModal-body',
      }}
      onClose={
        bulkActionReassign?.isSubmitting
          ? () => {}
          : handleCloseBulkActionReassign
      }
      isOpen={bulkActionReassign?.isOpen}
      headerIcon={<Icon kind='AlertWarningFilledSeparateColors' size='large' />}
      title={
        <BulkActionReassignHeader
          id={bulkActionReassign?.id}
          bulkFooterStats={bulkFooterStats}
        />
      }
      subtitle={
        <BulkActionReassignSubTitle
          id={bulkActionReassign?.id}
          bulkFooterStats={bulkFooterStats}
        />
      }
      contentBody={
        <BulkActionReassignBody
          bulkFooterStats={bulkFooterStats}
          bulkActionReassign={bulkActionReassign}
          setBulkActionReassign={setBulkActionReassign}
          handleCloseBulkActionReassign={handleCloseBulkActionReassign}
          handleCloseBukActionReassignAlert={handleCloseBukActionReassignAlert}
          bulkPredefinedComments={bulkPredefinedComments}
        />
      }
      FooterActionBarComp={
        <BulkActionReassignFooter
          handleReassign={handleReassign}
          bulkActionReassign={bulkActionReassign}
          handleCloseBulkActionReassign={handleCloseBulkActionReassign}
        />
      }
    />
  );
}

BulkActionReassign.propTypes = {
  bulkFooterStats: PropTypes.shape({}),
  bulkActionReassign: PropTypes.shape(BULK_ACTION_REASSIGN).isRequired,
  setBulkActionReassign: PropTypes.func.isRequired,
  handleReassign: PropTypes.func.isRequired,
  bulkPredefinedComments: PropTypes.arrayOf(PropTypes.string),
  isMobileXlargeWidth: PropTypes.bool,
};

BulkActionReassign.defaultProps = {
  bulkFooterStats: {},
  bulkPredefinedComments: [],
  isMobileXlargeWidth: false,
};

export default BulkActionReassign;
